import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { HiDocumentText } from "react-icons/hi2";
import { Skeleton, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material";

import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import SectionTopicHeader from "../../components/common/SectionTopicHeader";
import { routes } from "../../helper/routes";
import PaperReviewList from "./PaperReviewList";
import AssessmentPagination from "./AssessmentPagination";
import Filter from "./Filter";
import PaperReviewViewPage from "./PaperReviewViewPage";
import AssignDrawer from "../../components/common/AssignDrawer";
import { sweetConfirmAlert, sweetSuccessAlert, sweetErrorAlert } from "../../components/common/SweetAlert";

import AssessLibSectionsProvider, {
  useAssessmentLibrarySections,
} from "../../context/assessSections/AssessLibSectionsContext";
import { assign_reviewer, assign_proofreader, resetAssigner, reset_assessment_review, updateAssessmentStatus } from "../../service/assessSections/assessSectionsService/assessSectionsService";


import "./AssessShared.css";
import "./PaperReview.css";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Normalize a raw assessment API item into the shape the UI needs. */

const mapAssessmentToReview = (item) => {
  const first = item.first_name ?? "";
  const last = item.last_name ?? "";
  const createdByName =
    first || last
      ? `${first} ${last}`.trim()
      : item.assigned_to ?? "Unassigned";


  const rawReview = item.review_state;
  let latestReview = null;

  if (Array.isArray(rawReview) && rawReview.length) {
    latestReview = rawReview.find((r) => r.is_latest) || rawReview[rawReview.length - 1];
  } else if (rawReview && typeof rawReview === "object") {
    latestReview = rawReview;
  }

  const rawOperation = item.operation_state;
  let latestOperation = null;

  if (Array.isArray(rawOperation) && rawOperation.length) {
    latestOperation =
      rawOperation.find((entry) => entry.is_operation_latest) ||
      rawOperation[rawOperation.length - 1];
  } else if (rawOperation && typeof rawOperation === "object") {
    latestOperation = rawOperation;
  }

  const assessmentState = String(item.assessment_state || "").toLowerCase();

  const isOperationWorkflow =
    assessmentState === "proofreading" ||
    assessmentState === "dataentryverification";

  const assignedSource = isOperationWorkflow ? latestOperation : latestReview;

  return {
    id: item.assessment_id,
    code: item.assessment_id ? `ASMNT${item.assessment_id}` : "N/A",
    name: item.title ?? "Untitled Assessment",
    time: item.start_time ?? "Not scheduled",
    status: item.assessment_state,
    createByName: createdByName,
    createdAt: item.created_at,
    grade: item.grade_id,
    language: item.language_id,
    subject: item.subject_id,
    term: item.term_id,
    author: item.author_id,
    chapter: item.chapter_id,
    difficulty: item.difficulty_id,
    examCategory: item.exam_category_id,
    source: item.source_id,
    createdBy: item.createdBy,
    year: item.year_id,
    noOfParts: item.no_of_parts,
    isPause: item.is_pause,
    isRestart: item.is_restart,
    isSetTimeLimit: item.is_set_time_limit,
    examTimeInterval: item.exam_time_interval,
    bufferTime: item.buffer_time,

    additional_info: item.additional_info,
    attempt_no_id: item.attempt_no_id,
    buffer_time_unit_id: item.buffer_time_unit_id,
    exam_time_interval_unit_id: item.exam_time_interval_unit_id,
    grading_method_id: item.grading_method_id,
    is_multiple: item.is_multiple,
    parent_assessment_id: item.parent_assessment_id,
    user_role_id: item.user_role_id,
    terms_content: item.terms_content,
    status_id: item.status_id,


    review_state_latest: latestReview,

    // keep the raw review_state so callers can inspect array/object form
    review_state: item.review_state,
    assignedTo: latestReview?.teacher_name ?? item.operation_state?.[0]?.teacher_name,
    assignedBy: assignedSource?.assigned_by_name || "N/A",
    operationCompleted: latestOperation?.operation_completed ?? false,
    operationStatusText: isOperationWorkflow
      ? latestOperation?.operation_completed
        ? "Completed"
        : "InProgress"
      : "",
    assignedToId: latestReview?.teacher_id ?? item.operation_state?.[0]?.reviewer_id,
    workflowStatus: isOperationWorkflow
      ? latestOperation?.status_name
      : latestReview?.status,
    workflowStatusId: isOperationWorkflow
      ? latestOperation?.status_id
      : latestReview?.status_id,


    // operation_state may be an array; keep as-is for callers
    operation_state: item.operation_state,
    operation_state_latest: latestOperation,

    commentsCount: 0,
    comments: [],


  };
};

const getEmployeeDisplayName = (options = [], employeeId) => {
  const matchedOption = options.find((option) => String(option?.value) === String(employeeId));

  if (!matchedOption) {
    return employeeId ? String(employeeId) : "";
  }

  const userCode = matchedOption?.meta?.user_code || "";
  const firstName = matchedOption?.meta?.first_name || matchedOption?.meta?.firstname || matchedOption?.meta?.firstName || "";
  const lastName = matchedOption?.meta?.last_name || matchedOption?.meta?.lastname || matchedOption?.meta?.lastName || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ");

  return fullName || matchedOption?.label || String(employeeId);
};

const buildAssignedPaperUpdate = (paper, assignOperation, assignEmployee, responseData, metaDataOptions) => {
  if (!paper) return paper;

  const response = responseData ?? {};
  const reviewerOptions = metaDataOptions?.ReviewersOptions ?? [];
  const proofreaderOptions = metaDataOptions?.assignDrawerCreatedByOptions ?? [];
  const employeeOptions = assignOperation === "3" ? reviewerOptions : proofreaderOptions;
  const assignedTo = getEmployeeDisplayName(employeeOptions, assignEmployee);

  if (assignOperation === "3") {
    const reviewResult = response.assessment_review_new ?? {};

    const nextReviewState = {
      ...reviewResult,
      teacher_id: reviewResult.teacher_id ?? (Number(assignEmployee) || assignEmployee),
      teacher_name: assignedTo,
      is_latest: true,
      status_id: reviewResult.status_id ?? 3,
      status: reviewResult.status ?? "Review",
    };

    const existingReviews = Array.isArray(paper.review_state)
      ? paper.review_state
      : paper.review_state
        ? [paper.review_state]
        : [];

    return {
      ...paper,
      status: "Review",
      status_id: nextReviewState.status_id,
      assignedTo,
      assignedToId: nextReviewState.teacher_id,
      review_state: [
        ...existingReviews.map((review) => ({
          ...review,
          is_latest: false,
        })),
        nextReviewState,
      ],
      review_state_latest: nextReviewState,
      workflowStatus: nextReviewState.status,
      workflowStatusId: nextReviewState.status_id ?? 3,
    };
  }

  if (assignOperation === "9") {
    const operationResult = response.assessment_operation ?? {};
    const nextOperationState = {
      ...operationResult,
      employee_id: operationResult.employee_id ?? (Number(assignEmployee) || assignEmployee),
      teacher_name: assignedTo,
      is_operation_latest: true,
      status_name: operationResult.status_name ?? "Proofreading",
    };

    const existingOperations = Array.isArray(paper.operation_state)
      ? paper.operation_state
      : paper.operation_state
        ? [paper.operation_state]
        : [];

    return {
      ...paper,
      status: "ProofReading",
      status_id: nextOperationState.status_id ?? nextOperationState.operation_id ?? 9,
      assignedTo,
      assignedToId: nextOperationState.employee_id,
      operation_state: [...existingOperations.map((operation) => ({ ...operation, is_operation_latest: false })), nextOperationState],
      operation_state_latest: nextOperationState,
      workflowStatus: nextOperationState.status_name,
      workflowStatusId: nextOperationState.status_id ?? nextOperationState.operation_id ?? 9,
    };
  }

  return paper;
};


// ─────────────────────────────────────────────────────────────────────────────
// Inner component (consumes context)
// ─────────────────────────────────────────────────────────────────────────────

function PaperReviewSectionContent() {
  const history = useHistory();

  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedPaper, setSelectedPaper] = useState(null);

  const [isAssigning, setIsAssigning] = useState(false);
  const [assignSelectedPaper, setAssignSelectedPaper] = useState(null);
  const [assignOperation, setAssignOperation] = useState("");
  const [assignEmployee, setAssignEmployee] = useState("");
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedPaperForStatus, setSelectedPaperForStatus] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");

  // console.log("Filtered items:", filteredItems);
  const {
    activeTab,
    setActiveTab,
    metaDataOptions,
    filters,
    setFilters,
    AllassessmentData,
    myAssessmentData,
    isAssessmentDataLoading,
    assessmentDataError,
    totalAssessCount,
    myAssessTotalCount,
    onPageChange,
    onPageSizeChange,
    employeeType,
    isViewingPaper,
    setIsViewingPaper,
  } = useAssessmentLibrarySections();

  const handleResetReview = useCallback(async (item) => {
    try {
      await sweetConfirmAlert(
        "Reset Review Details",
        "Are you sure you want to clear all review details and reset this assessment to Ready?",
        "warning"
      );

      const latestReviewState = item?.review_state_latest || item?.review_state?.[0] || null;
      const latestOperationState = item?.operation_state_latest || item?.operation_state?.[0] || null;

      const resetAssignerPayload = {
        asmnt_review_id: latestReviewState?.asmnt_review_id ?? null,
        asmnt_operation_id: latestOperationState?.asmnt_operation_id ?? null,
      };

      if (resetAssignerPayload.asmnt_review_id || resetAssignerPayload.asmnt_operation_id) {
        const assignerResetRes = await resetAssigner(item.id, resetAssignerPayload);

        if (!assignerResetRes?.success) {
          throw new Error(assignerResetRes?.message || "Failed to reset assignment.");
        }
      }

      const reviewResetRes = await reset_assessment_review(item.id);

      if (!reviewResetRes?.success) {
        throw new Error(reviewResetRes?.message || "Failed to reset review progress.");
      }

      const statusRes = await updateAssessmentStatus(item.id, {
        status: "Ready",
      });

      if (!statusRes?.success) {
        throw new Error(statusRes?.message || "Failed to update assessment status to Ready.");
      }

      setFilteredItems((prev) =>
        prev.map((paper) =>
          paper.id === item.id
            ? {
              ...paper,
              status: "Ready",
              status_id: null,
              assignedTo: "N/A",
              assignedToId: null,
              workflowStatus: null,
              workflowStatusId: null,
              review_state: [],
              review_state_latest: null,
              operation_state: [],
              operation_state_latest: null,
            }
            : paper
        )
      );

      setFilters((prev) => ({ ...prev }));
      await sweetSuccessAlert("Assessment reset to Ready successfully!");
    } catch (err) {
      if (err === undefined) return;
      console.error("Error resetting review:", err);
      sweetErrorAlert("Reset failed", err?.message || "Unable to reset the assessment.");
    }
  }, [setFilters]);

  const handleUpdateStatusClick = useCallback((item) => {
    setSelectedPaperForStatus(item);
    setSelectedStatus(item?.status || "");
    setOpenStatusDialog(true);
  }, []);

  const handleUpdateStatusSubmit = useCallback(async () => {
    if (!selectedPaperForStatus || !selectedStatus) return;

    try {
      if (selectedStatus === "Ready") {
        setOpenStatusDialog(false);

        await handleResetReview(selectedPaperForStatus);

        setSelectedPaperForStatus(null);
        setSelectedStatus("");
        return;
      }

      const res = await updateAssessmentStatus(selectedPaperForStatus.id, {
        status: selectedStatus,
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update assessment status.");
      }

      setFilteredItems((prev) =>
        prev.map((paper) =>
          paper.id === selectedPaperForStatus.id
            ? {
              ...paper,
              status: selectedStatus,
            }
            : paper
        )
      );

      setOpenStatusDialog(false);
      setSelectedPaperForStatus(null);
      setSelectedStatus("");

      setFilters((prev) => ({ ...prev }));
      await sweetSuccessAlert("Assessment status updated successfully!");
    } catch (err) {
      if (err === undefined) return;
      console.error("Error updating status:", err);
      sweetErrorAlert("Update failed", err?.message || "Unable to update status.");
    }
  }, [
    selectedPaperForStatus,
    selectedStatus,
    handleResetReview,
    setFilters,
  ]);
  const statusOptions = [
    { value: "Ready", label: "Ready" },
    { value: "ReferBack", label: "Refer Back" },
    { value: "ReviewCompleted", label: "Review Completed" },
  ];

  // ── Keep API `selectedCreators` filter in sync with active tab ───────────
  useEffect(() => {
    const source =
      activeTab === "my"
        ? myAssessmentData
        : AllassessmentData;

    setFilteredItems(source?.map(mapAssessmentToReview) ?? []);
  }, [activeTab, myAssessmentData, AllassessmentData]);

  const totalCount = activeTab === "my" ? myAssessTotalCount : totalAssessCount;

  // ── Callbacks ────────────────────────────────────────────────────────────
  const handleFilterQueryChange = useCallback(
    (apiQuery) => {
      setFilters((prev) => ({ ...prev, ...apiQuery, offset: 0 }));
    },
    [setFilters],
  );


  const closePaper = useCallback(() => {
    setIsViewingPaper(false);
    setSelectedPaper(null);
  }, []);

  const openPaper = useCallback((item) => {
    setSelectedPaper(item);
    setIsViewingPaper(true);
  }, []);

  const openAssign = useCallback((item) => {
    setAssignSelectedPaper(item);

    const assessmentState = String(item?.status || "").toLowerCase();
    const isOperationWorkflow =
      assessmentState === "proofreading" ||
      assessmentState === "dataentryverification";

    const latest = isOperationWorkflow
      ? item.operation_state_latest
      : item.review_state_latest;

    const statusId = Number(latest?.status_id ?? latest?.statusId ?? 0);
    const statusName = String(latest?.status ?? latest?.status_name ?? "").toLowerCase();

    let op = "";

    if (statusId === 3 || statusName.includes("review")) {
      op = "3";
    } else if (statusId === 9 || statusName.includes("proofread")) {
      op = "9";
    }

    setAssignOperation(op);

    const emp =
      latest?.employee_id ??
      latest?.reviewer_id ??
      latest?.teacher_id ??
      "";

    setAssignEmployee(emp ? String(emp) : "");
    setIsAssigning(true);
  }, []);



  const handleResetAssigner = useCallback(async (item) => {
    try {

      await sweetConfirmAlert(
        "Reset assignment",
        "Are you sure you want to reset this assignment?",
        "warning",
      );


      const latestReviewState = Array.isArray(item?.review_state_latest)
        ? item.review_state_latest[0]
        : item?.review_state_latest;
      const latestOperationState = Array.isArray(item?.operation_state_latest)
        ? item.operation_state_latest[0]
        : item?.operation_state_latest;

      const payload = {
        asmnt_review_id: latestReviewState?.asmnt_review_id ?? item?.review_state?.[0]?.asmnt_review_id ?? null,
        asmnt_operation_id: latestOperationState?.asmnt_operation_id ?? item?.operation_state?.[0]?.asmnt_operation_id ?? null,
      };

      const res = await resetAssigner(item?.id, payload);

      if (!res?.success) {
        throw new Error(res?.message || "Failed to reset assignment.");
      }


      if (payload.asmnt_review_id) {
        setFilteredItems((prev) =>
          prev.map((it) => {
            if (it.id !== item.id) return it;

            const prevReviews = Array.isArray(it.review_state_latest) ? it.review_state_latest : [];
            const clearedPrevReviews = prevReviews.map((r) => ({ ...r, is_latest: false }));

            return {
              ...it,
              assignedTo: "",
              assignedToId: null,
              review_state: clearedPrevReviews,
              review_state_latest: null,
              operation_state_latest: it.operation_state_latest,
              workflowStatus: null,
              workflowStatusId: null,
              status: "Ready"
            };
          }),
        );
      } else if (payload.asmnt_operation_id) {
        setFilteredItems((prev) =>
          prev.map((it) => {
            if (it.id !== item.id) return it;
            return {
              ...it,
              assignedTo: "",
              assignedToId: null,
              review_state: it.review_state,
              review_state_latest: it.review_state_latest,
              operation_state: null,
              operation_state_latest: null,
              workflowStatus: null,
              workflowStatusId: null,
              status: "Ready"
            };
          }),
        );
      }


      // If the assign drawer was open for this paper, clear it too
      setAssignSelectedPaper();
      setAssignSelectedPaper(null);
      setAssignOperation("");
      setAssignEmployee("");

      await sweetSuccessAlert("Assignment reset successfully!");
    } catch (err) {
      // sweetConfirmAlert rejects on cancel — treat that as no-op
      if (err === undefined) return;
      console.error("Error resetting assignment:", err);
      sweetErrorAlert("Reset failed", err?.message || "Unable to reset the assignment.");
    }
  }, []);

  const closeAssign = useCallback(() => {
    setIsAssigning(false);
    setAssignSelectedPaper(null);
    setAssignOperation("");
    setAssignEmployee("");
  }, []);


  const handleAssignSubmit = useCallback(async () => {
    if (!assignSelectedPaper?.id) {
      sweetErrorAlert("Assignment failed", "No paper was selected for assignment.");
      return;
    }

    if (!assignOperation) {
      sweetErrorAlert("Assignment failed", "Please select an operation before submitting.");
      return;
    }

    if (!assignEmployee) {
      sweetErrorAlert("Assignment failed", "Please select an employee before submitting.");
      return;
    }

    try {
      if (assignOperation === "9") {
        const payload_proofreader = {
          selected_operator: assignEmployee,
          selected_operation: assignOperation,
        };

        const res = await assign_proofreader(assignSelectedPaper.id, payload_proofreader);
        if (!res?.success) {
          throw new Error(res?.message || "Failed to assign proofreader.");
        }

        const responseData = res?.data ?? res;
        const updatedPaper = buildAssignedPaperUpdate(
          assignSelectedPaper,
          assignOperation,
          assignEmployee,
          responseData,
          metaDataOptions,
        );

        setFilteredItems((prev) =>
          prev.map((item) => (item.id === assignSelectedPaper.id ? updatedPaper : item)),
        );
        setSelectedPaper((prev) => (prev?.id === assignSelectedPaper.id ? updatedPaper : prev));
        setAssignSelectedPaper(updatedPaper);

        await sweetSuccessAlert("Proofreader assigned successfully!");

        closeAssign();
        return;
      }

      if (assignOperation === "3") {
        const payload_reviewer = {
          selected_reviewer: assignEmployee,
        };

        const res = await assign_reviewer(assignSelectedPaper.id, payload_reviewer);
        if (!res?.success) {
          throw new Error(res?.message || "Failed to assign reviewer.");
        }

        const responseData = res?.data ?? res;
        const updatedPaper = buildAssignedPaperUpdate(
          assignSelectedPaper,
          assignOperation,
          assignEmployee,
          responseData,
          metaDataOptions,
        );

        setFilteredItems((prev) =>
          prev.map((item) => (item.id === assignSelectedPaper.id ? updatedPaper : item)),
        );
        setSelectedPaper((prev) => (prev?.id === assignSelectedPaper.id ? updatedPaper : prev));
        setAssignSelectedPaper(updatedPaper);

        await sweetSuccessAlert("Reviewer assigned successfully!");


        closeAssign();
        return;
      }

      sweetErrorAlert("Assignment failed", "Unsupported assignment operation selected.");
    } catch (err) {
      console.error("Error assigning paper:", err);
      sweetErrorAlert("Assignment failed", err?.message || "Unable to complete the assignment.");
    }
  }, [assignSelectedPaper, assignOperation, assignEmployee, closeAssign, metaDataOptions]);

  // const openPaper = (item) => {
  //   history.push({
  //     pathname: routes.ppr_rew_view.replace(":id", item.id),
  //     state: {
  //       metaDataOptions,
  //     },
  //   });
  // };

  // ── Derived pagination values ────────────────────────────────────────────
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;

  // ── Render ───────────────────────────────────────────────────────────────

  if (isViewingPaper && selectedPaper) {
    return (
      <PaperReviewViewPage
        item={selectedPaper}
        EmployeeType={employeeType}
        paperId={selectedPaper.id}
        metaDataOptions={metaDataOptions}
        onBack={closePaper}
      />
    );
  }


  return (
    <ContentWrapper>

      <section className="assess-section paper-review-section">
        <SectionTopicHeader
          className="assess-topic-header"
          title="Paper Review"
          subtitle="Review assigned papers and proof-reading progress"
          icon={<HiDocumentText />}
          color="#734EBC"
          colorAlt="#D97706"
        />

        {/* Tabs */}
        <div className="question-tabs">
          <div
            className="assess-tabs"
            role="tablist"
            aria-label="Paper review tabs"
          >
            {[
              ...(employeeType === "TEACH" || employeeType === "DEOP" ? [{ key: "my", label: "My Papers" }] : []),
              ...(employeeType === "MASAD" || employeeType === "DEOP" ? [{ key: "all", label: "All Papers" }] : []),
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeTab === key}
                className={`assess-tab ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Filter bar */}
        <Filter
          metaDataOptions={metaDataOptions}
          onQueryChange={handleFilterQueryChange}
          apiFilters={filters}
          syncKey={activeTab}
          count={isAssessmentDataLoading ? 0 : totalCount}
          countLabel="No of Papers"
          searchAriaLabel="Search papers"
        />

        {/* Status messages loder*/}
        {isAssessmentDataLoading && (
          <div role="status" aria-label="Loading papers">
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Skeleton variant="rounded" width="100%" height={48} />
              <Skeleton variant="rounded" width="100%" height={72} />
              <Skeleton variant="rounded" width="100%" height={72} />
            </Stack>
          </div>
        )}

        {assessmentDataError && (
          <div className="alert alert-danger" role="alert">
            Error loading papers: {String(assessmentDataError)}
          </div>
        )}

        {!isAssessmentDataLoading &&
          !assessmentDataError &&
          filteredItems.length === 0 && (
            <div className="assess-empty" role="status">
              No papers found for the selected criteria.
            </div>
          )}

        {/* List */}
        {!isAssessmentDataLoading && (
          <PaperReviewList
            employeeType={employeeType}
            items={filteredItems}
            onView={openPaper}
            onAssign={openAssign}
            onResetAssign={handleResetAssigner}
            onResetReview={handleResetReview}
            onUpdateStatus={handleUpdateStatusClick}
            metaDataOptions={metaDataOptions}
          />
        )}

        {/* Pagination */}
        <AssessmentPagination
          page={currentPage}
          pageSize={filters.limit}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
        <AssignDrawer
          open={isAssigning}
          paper={assignSelectedPaper}
          metaDataOptions={metaDataOptions}
          operationValue={assignOperation}
          employeeValue={assignEmployee}
          onClose={closeAssign}
          onOperationChange={setAssignOperation}
          onEmployeeChange={setAssignEmployee}
          onSubmit={handleAssignSubmit}
        />
        <Dialog
          open={openStatusDialog}
          onClose={() => setOpenStatusDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Change Assessment Status</DialogTitle>
          <DialogContent>
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel id="status-select-label">Select Status</InputLabel>
              <Select
                labelId="status-select-label"
                value={selectedStatus}
                label="Select Status"
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdateStatusSubmit} disabled={!selectedStatus}>
              Update Status
            </Button>
          </DialogActions>
        </Dialog>
      </section>
    </ContentWrapper>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Public export — wraps inner component with its required context provider
// ─────────────────────────────────────────────────────────────────────────────
export default function PaperReviewSection() {
  return (
    <AssessLibSectionsProvider>
      <PaperReviewSectionContent />
    </AssessLibSectionsProvider>
  );
}
